import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const relationOptions = [
  "Brother",
  "Sister",
  "Cousin",
  "Uncle",
  "Aunt",
  "Guardian",
  "Grandfather",
  "Grandmother",
];

const EnrollGuardian = () => {
  const storedSessionId = localStorage.getItem("currentSessionId");
  const [selectedSession] = useState(storedSessionId || "");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [guardians, setGuardians] = useState([{ name: "", image: "", phone: "", relation: "" }]);
  const [studentsGuardians, setStudentsGuardians] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parentID, setParentID] = useState(null);
  
  const navigate = useNavigate();

  const labelStyle = "text-black font-semibold text-[18px] mb-2";
  const inputStyle =
    "w-full px-3 py-2 border rounded-lg outline-none text-[14px] focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400 placeholder:text-[14px]";

  // =========================
  // Fetch Classes by Session
  // =========================
  useEffect(() => {
    if (!storedSessionId) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${storedSessionId}`)
      .then((res) => setClasses(res.data))
      .catch((err) => console.error("Error fetching classes:", err));
  }, [selectedSession]);

  // =========================
  // Fetch Sections by Class & Session
  // =========================
  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
          selectedClass
        )}/getSectionsByClassName?sessionId=${storedSessionId}`
      )
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.sections || [];
        setSections(data);
        setSelectedSection("");
      })
      .catch((err) => {
        console.error("Failed to fetch sections:", err);
        setSections([]);
      });
  }, [selectedClass, selectedSession]);

 // =========================
// Fetch Students by Class, Section & Session
// =========================
useEffect(() => {
  if (!selectedClass || !selectedSection || !selectedSession) {
    setStudents([]);
    return;
  }

  axios
    .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
      params: {
        class: selectedClass,
        section: selectedSection,
        sessionId: storedSessionId, // <-- filter by session
        status: "active",
      },
    })
    .then((res) => setStudents(res.data))
    .catch((err) => console.error("Error fetching students:", err));
}, [selectedClass, selectedSection, storedSessionId]);

  // =========================
  // Fetch selected student's parentID
  // =========================
  useEffect(() => {
    if (!selectedStudent) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents/${selectedStudent}`)
      .then((res) => setParentID(res.data.parentID))
      .catch((err) => console.error("Error fetching student details:", err));
  }, [selectedStudent]);

  // =========================
  // Fetch all guardians
  // =========================
  const fetchGuardians = () => {
    if (!storedSessionId) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/guardians/getAllGuardians`, {
      params: { sessionId: storedSessionId }, 
    })
      .then((res) => setStudentsGuardians(res.data))
      .catch((err) => console.error("Error fetching student & guardian data:", err));
  };

  useEffect(() => {
    fetchGuardians();
  }, []);

  // =========================
  // Handle Guardian Form Changes
  // =========================
  const handleGuardianChange = (index, field, value) => {
    const updatedGuardians = [...guardians];
    updatedGuardians[index][field] = value;
    setGuardians(updatedGuardians);
  };

  const addGuardian = () =>
    setGuardians([...guardians, { name: "", image: "", phone: "", relation: "" }]);

  const removeGuardian = (index) =>
    setGuardians(guardians.filter((_, i) => i !== index));

  // =========================
  // Edit Guardian
  // =========================
  const handleEdit = (studentId) => {
    const data = studentsGuardians.find((d) => d.studentId === studentId);
    if (data) {
      setEditing(true);
      setEditingStudentId(studentId);
      setSelectedClass(data.class);
      setSelectedSection(data.section);
      setSelectedStudent(data.studentId);
      setGuardians(data.guardians);
    }
  };

  // =========================
  // Submit Form
  // =========================
  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  if (!selectedStudent) {
    alert("Please select student.");
    setLoading(false);
    return;
  }

  // Check if all guardian fields are filled
  const invalid = guardians.find(
    (g) => !g.name || !g.phone || !g.relation || !g.image
  );
  if (invalid) {
    alert("All guardian fields are required.");
    setLoading(false);
    return;
  }

  const studentDetails = students.find((s) => s._id === selectedStudent);
  if (!studentDetails) {
    alert("Student details not found.");
    setLoading(false);
    return;
  }

  const formData = new FormData();
  formData.append("studentId", selectedStudent);
  formData.append("class", selectedClass);
  formData.append("section", selectedSection);
  formData.append("parentID", parentID);
  formData.append("studentName", `${studentDetails.firstName} ${studentDetails.lastName}`);
  formData.append("sessionId", storedSessionId); // <-- MUST add this

  guardians.forEach((g, index) => {
    formData.append(`guardians[${index}][name]`, g.name);
    formData.append(`guardians[${index}][relation]`, g.relation);
    formData.append(`guardians[${index}][phone]`, g.phone);
    formData.append(`guardians[${index}][image]`, g.image); // must be File object
  });

  try {
    if (editing) {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/guardians/updateGuardians/${editingStudentId}`,
        formData
      );
      alert("Guardian updated successfully!");
    } else {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/guardians/addGuardians`,
        formData
      );
      alert("Guardian added successfully!");
    }
    fetchGuardians();
    clearForm();
  } catch (error) {
    console.error("Error submitting:", error);
    alert("Error submitting guardian data. Check all fields and images.");
  } finally {
    setLoading(false);
  }
};

  // =========================
  // Delete Guardian
  // =========================
  const handleDelete = async (studentId) => {
    if (!window.confirm("Are you sure?")) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/guardians/deleteGuardians/${studentId}`);
      setStudentsGuardians((prev) => prev.filter((s) => s._id !== studentId));
      alert("Deleted successfully!");
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  // =========================
  // Clear Form
  // =========================
  const clearForm = () => {
    setEditing(false);
    setEditingStudentId(null);
    setSelectedClass("");
    setSelectedSection("");
    setSelectedStudent("");
    setGuardians([{ name: "", image: "", phone: "", relation: "" }]);
  };

  // =========================
  // JSX
  // =========================
  const filteredGuardians = studentsGuardians.filter((data) => {
  if (!selectedClass && !selectedSection) return true;

  if (selectedClass && selectedSection) {
    return data.class === selectedClass && data.section === selectedSection;
  }

  if (selectedClass) {
    return data.class === selectedClass;
  }

  return true;
});

  return (
    <div className="max-w-full mx-auto p-4 sm:p-8 bg-white">
      <form onSubmit={handleSubmit}>
        {/* Student Selection */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Class */}
          <div className="flex-1">
            <label className={labelStyle}>Class Name:</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className={inputStyle}
            >
              <option value="">Select Class</option>
              {classes.map((cls) => (
                <option key={cls._id} value={cls.className}>
                  {cls.className}
                </option>
              ))}
            </select>
          </div>

          {/* Section */}
          <div className="flex-1">
            <label className={labelStyle}>Section Name:</label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              disabled={!selectedClass}
              className={inputStyle}
            >
              <option value="">Select Section</option>
              {sections.map((sec) => (
                <option key={sec._id} value={sec.sectionName}>
                  {sec.sectionName}
                </option>
              ))}
            </select>
          </div>

          {/* Student */}
          <div className="flex-1">
            <label className={labelStyle}>Student:</label>
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              disabled={!selectedSection}
              className={inputStyle}
            >
              <option value="">Select Student</option>
              {students.map((stu) => (
                <option key={stu._id} value={stu._id}>
                  {stu.firstName} {stu.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Guardian Details */}
        <h3 className="font-bold text-[18px] mb-4 text-black">Guardian Details</h3>

        <div className="flex flex-col gap-4">
          {guardians.map((guardian, index) => (
            <div
              key={index}
              className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-lg shadow-sm"
            >
              <input
                type="text"
                placeholder="Name"
                value={guardian.name}
                onChange={(e) => handleGuardianChange(index, "name", e.target.value)}
                className={inputStyle}
              />
              <input
                type="file"
                onChange={(e) => handleGuardianChange(index, "image", e.target.files[0])}
                className={inputStyle}
              />
              <input
                type="number"
                placeholder="Phone Number"
                value={guardian.phone}
                onChange={(e) => handleGuardianChange(index, "phone", e.target.value)}
                className={inputStyle}
              />
              <select
                value={guardian.relation}
                onChange={(e) => handleGuardianChange(index, "relation", e.target.value)}
                className={inputStyle}
              >
                <option value="">Select Relation</option>
                {relationOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeGuardian(index)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addGuardian}
          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 mt-4"
        >
          Add More
        </button>

        {/* Buttons */}
        <div className="flex flex-col md:flex-row gap-4 mt-6">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? "Submitting..." : editing ? "Update" : "Submit"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={clearForm}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Table */}
      <h2 className="font-bold mt-10 mb-4 text-black text-center !text-[18px]">
        Student and Guardian Details
      </h2>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full bg-white border text-center">
          <thead className="bg-gray-200">
            <tr>
              {["Sr No.", "Name", "Class", "Action"].map((head) => (
                <th
                  key={head}
                  className="px-4 py-2 border text-[18px] font-semibold text-black"
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-gray-50">
            {studentsGuardians.map((data, index) => (
              <tr key={data.studentId} className="hover:bg-gray-100">
                <td className="px-4 py-2 border text-[14px]">{index + 1}</td>
                <td className="px-4 py-2 border text-[14px]">{data.studentName}</td>
                <td className="px-4 py-2 border text-[14px]">
                  {data.class} ({data.section})
                </td>
                <td className="px-4 py-2 border">
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      onClick={() => navigate(`/view-guardian/${data.studentId}`)}
                      className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-500"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(data.studentId)}
                      className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-400"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(data._id)}
                      className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden flex flex-col gap-4">
        {studentsGuardians.map((data) => (
          <div key={data.studentId} className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="font-semibold text-[18px] text-black mb-1">{data.studentName}</h3>
            <p className="text-gray-700 text-[14px] mb-1">
              Class: {data.class} ({data.section})
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate(`/view-guardian/${data.studentId}`)}
                className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-500 flex-1"
              >
                View
              </button>
              <button
                onClick={() => handleEdit(data.studentId)}
                className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-400 flex-1"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(data._id)}
                className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500 flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnrollGuardian;
